import { useCallback } from 'react';
import { usePostTWPLoginMutation } from 'services/login';
import config from 'config';
import { hasValue, isNil, localStorage } from 'swipeclock-frontend';
import { useLoginContext } from 'providers/LoginProvider';
import { hardNavigate, updateSearchParam } from 'helpers/core/url';
import { useLazyClockConnectQuery } from 'services/webclock';
import { useHistory } from 'react-router-dom';
import { useIsMobileMode } from './useMobileMode';
import { CREDENTIAL_SESSION_PARAM } from '../utils/webClockSession';

/**
 * Custom hook that returns a memoized callback for building a login or logout
 * redirect URL.
 *
 * @returns {Function} A memoized callback that constructs a login URL.
 *
 * @callback BuildLoginURL
 * @param {Object} params - Parameters used to construct the URL.
 * @param {string} [params.token] - JWT token to append as the `jwt` query parameter.
 * @param {boolean} [params.employeePortalLogin=false] - When `true`, builds a URL
 *   pointing to the ESS portal; otherwise points to the WorkForceHub URL.
 * @param {string|null} [params.altURL=null] - Override URL for the ESS portal base.
 *   Falls back to `config.urlESS` when not provided.
 * @param {string} [params.wfhURLLoc='webClock'] - The HTML entry filename
 * @param {boolean} [params.excludeJWTFromParams=false] - When `true`, removes any
 *   pre-existing `jwt` query parameter carried over from the current URL.
 * @returns {string} The fully constructed login redirect URL.
 */
export function useBuildLoginURL() {
  return useCallback(
    ({
      token,
      employeePortalLogin = false,
      altURL = null,
      wfhURLLoc = 'webClock',
      excludeJWTFromParams = false,
    }) => {
      // we want to add pre-existing query params to the url
      const params = new URLSearchParams(window.location.search);
      // credentialSession is internal WebClock state and must not follow
      // the user to ESS or another login URL.
      params.delete(CREDENTIAL_SESSION_PARAM);
      if (excludeJWTFromParams) {
        // In some instances such as when logging out we don't want to keep a
        // pre-existing jwt query param, however in many instances this param
        // doesn't actually exist.
        params.delete('jwt');
      }
      const searchParams = params.toString();

      // add a jwt token to the url
      const hasToken = hasValue(token);
      const tokenText = hasToken ? `?jwt=${token}` : '';

      // build the two base urls
      const wfhURL = `${config.embeddedTimeUIURL}/${wfhURLLoc}.html${tokenText}`;
      const essURL = `${altURL ?? config.urlESS}${tokenText}`;

      // we use the essURL when navigating to the employee portal and the
      // wfhURL otherwise.
      const baseLoginUrl = employeePortalLogin ? essURL : wfhURL;

      // add query params to the url if they exist
      const loginUrl = hasValue(searchParams)
        ? `${baseLoginUrl}${hasToken ? '&' : '?'}${searchParams}`
        : baseLoginUrl;

      return loginUrl;
    },
    []
  );
}

export function useOwnerCredentialsGrantLoginUser() {
  const [postLogin] = usePostTWPLoginMutation();

  return useCallback(async (values) => {
    const loginData = await postLogin({
      body: values,
    }).unwrap();

    if (hasValue(loginData.errorType)) {
      throw new Error(loginData.errorMessage);
    }

    const { roles, siteId, token, jwtStatus, siteName } = loginData;

    return { token, roles, jwtStatus, siteName, siteId };
  }, []);
}

/**
 * Custom hook that handles the complete login process for webclock users.
 * Manages authentication, site data storage, password change requirements,
 * and navigation to appropriate URLs based on login type. Uses RTK Query's
 * lazy clock connect query internally. Login type (webclock vs employee portal)
 * is determined by the isWebClockLogin state in LoginProvider context.
 *
 * @returns {Function} A callback function that handles the login process
 * @param {Object} params - Login parameters object
 * @param {Object} params.values - The object of values to send to the api
 *    The values object contains username, password and siteId keys.
 * @param {Function} params.setSubmitting - Function to set form submission state
 *
 * @description
 * This hook performs the following operations:
 * 1. Takes in username, password, and siteId for authentication
 * 2. Authenticates user credentials via loginUser
 * 3. Manages localStorage for site data persistence
 * 4. Handles password change requirements
 * 5. Navigates to appropriate URL (ESS or WebClock) based on LoginProvider state
 * 6. Uses useLazyClockConnectQuery for webclock users
 * 7. Handles and displays authentication errors
 *
 * @example
 * const handleLogin = useHandleLogin();
 *
 * await handleLogin({
 *   values: {
 *     username: 'qwerty',
 *     password: 'asdf',
 *     siteId: '12345',
 *   },
 *   setSubmitting: setIsSubmitting
 * });
 */
export function useHandleLogin() {
  const { isMobileMode } = useIsMobileMode();
  const sitesData = localStorage.getItem('SwipeClock.WebClock3.SiteID');
  const history = useHistory();
  const buildLoginUrl = useBuildLoginURL();
  const loginUser = useOwnerCredentialsGrantLoginUser();
  const [clockConnect] = useLazyClockConnectQuery();

  const {
    actions,
    meta: { pages },
  } = useLoginContext();

  return useCallback(
    async ({ values, setSubmitting, whiteLabelState, isWebClockLogin }) => {
      try {
        setSubmitting(true);
        const {
          siteId: currentSiteId,
          siteName,
          jwtStatus,
          token,
        } = await loginUser(values);
        let sitesArray = [];
        const newLocalStorageData = {};

        // determine what needs to be stored in localStorage sites array
        if (hasValue(sitesData)) {
          const parsedSiteData = JSON.parse(sitesData);
          switch (typeof parsedSiteData) {
            case 'object': {
              sitesArray = parsedSiteData?.sites || [];
              break;
            }
            // Backwards compatibility (old cookie only saved one site id (i.e. 7497))
            default:
              sitesArray = parsedSiteData
                ? [{ id: parsedSiteData, name: '' }]
                : [];
          }
          sitesArray = prepForLocalStorage(sitesArray, currentSiteId, siteName);
        } else if (hasValue(currentSiteId)) {
          sitesArray = [{ id: currentSiteId, name: siteName }];
        }

        // set the localStorage data
        newLocalStorageData.lastSiteUsed = currentSiteId;
        newLocalStorageData.sites = sitesArray;
        localStorage.setItem(
          'SwipeClock.WebClock3.SiteID',
          JSON.stringify(newLocalStorageData)
        );

        // Intercept to force password change if using unsecure password
        if (jwtStatus === 'PasswordChangeRequired') {
          const params = {
            jwt: token,
            username: values.username,
            password: values.password,
            site: currentSiteId,
          };

          // navigate to the reset password page
          actions.setCurrentPage(pages.RESETPASS);
          actions.setGlobalMessage();
          history.replace({
            pathname: '/clock/web/login/password/reset',
            state: params,
          });
          return;
        }

        if (!isWebClockLogin) {
          // navigate to the ess url for the employee portal:
          const loginUrl = buildLoginUrl({
            token,
            employeePortalLogin: true,
            altURL: whiteLabelState.urlESS,
          });
          if (typeof window !== 'undefined') {
            hardNavigate(loginUrl);
          }
        } else {
          const user = { token };
          const { data: clockConnectData, error: clockConnectError } =
            await clockConnect({
              user,
              mobile: isMobileMode,
            });

          if (hasValue(clockConnectError) || isNil(clockConnectData)) {
            throw new Error(
              clockConnectError?.message || 'Error: unable to access clock data'
            );
          }

          if (clockConnectData.errorCode === '-1') {
            throw new Error(clockConnectData.errorMessage);
          }

          updateSearchParam(CREDENTIAL_SESSION_PARAM, 'true');
          updateSearchParam('jwt', token);
          history.replace('/clock/web');
        }

        setSubmitting(false);
      } catch (error) {
        setSubmitting(false);

        console.error(error);
        const errorText = hasValue(error.message)
          ? error.message
          : `We're sorry, the email address, site id, or password you entered is not recognized.`;
        actions.setGlobalMessage({ message: errorText, severity: 'error' });
      }
    },
    [isMobileMode]
  );
}

/** helper functions */

/**
 * A function which takes in an array of sites, then adds a new site id and
 * name to the array if the id is not already present. Finally, it sorts the
 * array by site id and returns it.
 * @param {*} sitesArray the prior array of sites
 * @param {*} currentSiteId the current site id
 * @param {*} siteName the name of the site
 * @returns the updated array of sites
 */
const prepForLocalStorage = (sitesArray, currentSiteId, siteName) => {
  const siteMatch = sitesArray.filter((el) => {
    if (el.id === currentSiteId) {
      el.name = siteName;
      return true;
    }
    return false;
  });
  if (siteMatch.length === 0 && hasValue(currentSiteId)) {
    sitesArray.push({
      id: currentSiteId,
      name: siteName,
    });
  }
  const sortedArray = sitesArray
    .filter((site) => hasValue(site.id))
    .sort((a, b) => {
      return a.id - b.id;
    });

  return sortedArray;
};
